import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Minus, ArrowRight } from "lucide-react";

import { useCampusAuth } from "@/contexts/CampusAuth";
import { formatApiErrorDetail } from "@/lib/apiError";
import CinemaPageHero from "@/components/site/CinemaPageHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2-Tier-Strategie (Feb 2026 · Simplify): Pflege-KI Kompakt / Pflege-KI Team.
// Segmentierung nach Pflegedienst-Größe. Legacy-Tiers bleiben Backend-seitig für
// Migration bestehen, werden aber im UI nicht mehr angezeigt.
const HEADLINE_TIERS = ["pflege_ki_kompakt", "pflege_ki_team"];

// Anzeige-Label-Override für Karten + Tabellen (nicht-tarif-rechtsverbindlich,
// nur UI). Stripe `metadata.tier` bleibt unverändert.
const TIER_DISPLAY_LABEL = {
  pflege_ki_kompakt: "Pflege-KI Kompakt",
  pflege_ki_team: "Pflege-KI Team",
};

// Funktions-Vergleich · Alle Kern-Features sind IDENTISCH — der einzige
// Unterschied zwischen Kompakt und Team ist die Team-Größe.
const FEATURES = [
  { label: "Digitale Patientenmappe · 11 Sektionen", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "SIS · Themenfelder 1-6 mit Ressourcen & Bedarf", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Risikomatrix · 6 Assessments", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Medikation · Maßnahmenplan · Versorgungsauftrag", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "360°-Beobachtung · Vitalwerte", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "QPR-26 Prüfbogen · alle 26 Kriterien", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" }, highlight: true, sublabel: "7 Prüfbereiche · Original-Wortlaut GKV-Spitzenverband" },
  { label: "SIS-Sprachdiktat · Whisper", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Team-Chat pro Mappe", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Full-PDF-Export · Cover + 11 Sektionen + Signaturen", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Sammel-Audit-CSV · alle Mappen als Excel", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Digitale Freigabe mit Signatur-Pad", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Halluzinationsschutz · MDK-Schutzschild Premium", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" }, highlight: true, sublabel: "Live-Alarm + Direkt-Kanal zu Fachdozenten" },
  { label: "PWA · installierbar iOS/Android", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Prioritäts-Support · 24 h Antwortzeit", values: { pflege_ki_kompakt: "✓", pflege_ki_team: "✓" } },
  { label: "Mitarbeiter-Sub-Accounts", values: { pflege_ki_kompakt: "bis 10", pflege_ki_team: "unbegrenzt" }, highlight: true, sublabel: "Der einzige Unterschied zwischen den Tarifen" },
  { label: "1:1 Onboarding-Call für PDL + Kernteam", values: { pflege_ki_kompakt: "—", pflege_ki_team: "✓" } },
  { label: "Jahres-Pflichtfortbildungspaket", values: { pflege_ki_kompakt: "—", pflege_ki_team: "✓ inklusive" } },
];

const TIER_DESC = {
  pflege_ki_kompakt: {
    headline: "Für Pflegedienste bis 10 Mitarbeiter",
    bullets: [
      "1 PDL + bis zu 10 Sub-Accounts",
      "Unbegrenzt Patientenmappen",
      "QPR-26 · alle 26 Kriterien",
      "SIS-Diktat · Full-PDF-Export",
    ],
  },
  pflege_ki_team: {
    headline: "Für Pflegedienste ab 11 Mitarbeiter",
    bullets: [
      "Unbegrenzt Sub-Accounts",
      "Sammel-Audit-CSV",
      "Digitale Freigabe mit Signatur",
      "Prio-Support · Onboarding-Call",
    ],
  },
};


function ValueCell({ raw }) {
  if (raw === "✓") {
    return <Check className="h-4.5 w-4.5 mx-auto text-terra" strokeWidth={2.5} />;
  }
  if (raw === "—" || raw === "✗") {
    return <Minus className="h-4 w-4 mx-auto text-sand-400" strokeWidth={2} />;
  }
  return <span className="font-semibold text-sand-800 text-[13.5px]">{raw}</span>;
}


export default function Preise() {
  const { user } = useCampusAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/subscriptions/tiers`)
      .then((r) => setTiers(r.data?.tiers || []))
      .catch(() => setTiers([]));
  }, []);

  const tiersBySlug = useMemo(() => {
    const m = {};
    (tiers || []).forEach((t) => { m[t.slug] = t; });
    return m;
  }, [tiers]);

  const fmtPrice = (cents) => `${(cents / 100).toFixed(0)} €`;

  const startCheckout = async (slug) => {
    if (!user) {
      // Nicht eingeloggt → erst registrieren, danach zum Checkout
      navigate(`/campus/login?upgrade=${encodeURIComponent(slug)}`);
      return;
    }
    setBusy(slug);
    try {
      const { data } = await axios.post(
        `${API}/subscriptions/checkout`,
        { tier: slug, origin_url: window.location.origin },
        { withCredentials: true },
      );
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Checkout fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  if (!tiers) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sand-500 bg-cream-50">
        Lade Preise …
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 pb-24" data-testid="preise-page">
      {/* CINEMA HEADER */}
      <CinemaPageHero
        image="/images/cinema/preise-wide.jpg"
        imagePosition="center 40%"
        kicker="Pflege-KI Assistenz · Tarife"
        title="Ein Werkzeug."
        accent="Zwei Pakete."
        sub="Vom Einzelunternehmer bis zum Träger mit mehreren Standorten — wähle den Tarif, der zu deiner Rolle und deinem Umfang passt. Alle Preise netto, zzgl. 19 % USt. Monatlich kündbar, keine Setup-Gebühr."
        topPad="pt-32"
        testId="preise-cinema-hero"
      >
        <Link to="/" className="mt-8 inline-block text-[12px] uppercase tracking-[0.28em] font-semibold text-cream-100/70 hover:text-brand-rose transition-colors">
          ← Zurück zur Startseite
        </Link>
      </CinemaPageHero>
      <div className="pt-12" />

      {/* SCHNUPPER-STRIP · 29 € Kennenlernpaket für Pflegedienste */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto -mt-2 mb-8">
        <Link
          to="/pflege-ki-testen"
          data-testid="preise-schnupper-strip"
          className="block rounded-2xl border-2 border-terra bg-peach-50 hover:bg-peach-100 transition p-5 md:p-6"
        >
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-terra mb-1">
                🎁 Schnupper-Woche für Pflegedienste
              </div>
              <div className="text-sand-800 text-lg md:text-xl font-display font-semibold">
                <span className="text-terra">29 € einmalig</span> · 7 Tage Vollzugriff · Kein Abo
              </div>
              <div className="text-sand-600 text-[13.5px] mt-1">
                Voller Zugriff auf Patientenmappe · QPR-26 · SIS-Diktat · Team-Chat · Beispiel-Mappe inklusive
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-terra text-white px-5 py-2.5 text-sm font-semibold hover:bg-terra-dark">
              Test-Woche starten <ArrowRight className="h-4 w-4"/>
            </span>
          </div>
        </Link>
      </section>

      {/* 2 HEADLINE-TIERS · Cards */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-testid="preise-headline-cards">
          {HEADLINE_TIERS.map((slug) => {
            const t = tiersBySlug[slug];
            if (!t) return null;
            const isPro = slug === "pflege_ki_kompakt";
            return (
              <div
                key={slug}
                id={slug === "pflege_ki_pro" ? "pflege-ki" : slug === "pdl_suite_solo" ? "pdl-suite" : "single-held"}
                data-testid={`preise-card-${slug}`}
                className={`scroll-mt-28 rounded-2xl border bg-white p-6 flex flex-col ${
                  isPro ? "border-terra ring-2 ring-terra/30 relative" : "border-cream-300"
                }`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-6 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold bg-terra text-white">
                    Empfohlen
                  </div>
                )}
                <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-sand-500">
                  {TIER_DISPLAY_LABEL[slug] || t.tier}
                </div>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="font-display text-4xl text-sand-800 leading-none">{fmtPrice(t.amount_cents)}</span>
                  <span className="text-sand-500 text-[13px]">/ Monat</span>
                </div>
                <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-terra">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  Monatlich kündbar
                </div>
                <p className="mt-3 text-sand-700 text-[12.5px] font-semibold leading-snug">
                  {TIER_DESC[slug]?.headline || ""}
                  {t.max_seats == null && " · unbegrenzt Sub-Accounts"}
                  {typeof t.max_seats === "number" && t.max_seats > 1 && ` · bis ${t.max_seats} Sub-Accounts`}
                </p>
                <ul className="mt-3 space-y-1.5">
                  {(TIER_DESC[slug]?.bullets || []).map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2 text-[13px] text-sand-700 leading-snug"
                    >
                      <Check className="h-3.5 w-3.5 text-terra mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => startCheckout(slug)}
                  disabled={!!busy}
                  data-testid={`preise-cta-${slug}`}
                  className={`mt-5 h-11 rounded-full font-semibold text-[13.5px] inline-flex items-center justify-center gap-1.5 transition-colors ${
                    isPro
                      ? "bg-brand-darkred text-white hover:bg-brand-darkred/90"
                      : "bg-sand-800 text-white hover:bg-sand-700"
                  } ${busy === slug ? "opacity-60" : ""}`}
                >
                  {busy === slug ? "Wird vorbereitet …" : "Jetzt abonnieren"}
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-3 text-[11px] text-sand-500 leading-snug">
                  zzgl. 19 % USt · monatlich kündbar
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* VERGLEICHSTABELLE */}
      <section className="px-4 md:px-8 max-w-6xl mx-auto mt-12">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-sand-500 mb-2">
          Was ist enthalten?
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-sand-800 leading-tight mb-5">
          Funktionsvergleich
        </h2>

        <div className="overflow-x-auto rounded-2xl border border-cream-300 bg-white">
          <table className="w-full text-left" data-testid="preise-tabelle">
            <thead>
              <tr className="border-b border-cream-300 bg-cream-100/60">
                <th className="px-4 md:px-5 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold text-sand-600">
                  Funktion
                </th>
                {HEADLINE_TIERS.map((slug) => (
                  <th
                    key={slug}
                    scope="col"
                    className="px-3 md:px-4 py-3.5 text-center text-[11px] uppercase tracking-[0.22em] font-bold text-sand-600 min-w-[120px]"
                  >
                    <div className="text-sand-800">{TIER_DISPLAY_LABEL[slug] || tiersBySlug[slug]?.tier}</div>
                    <div className="text-[10.5px] text-terra mt-0.5">
                      {tiersBySlug[slug] && fmtPrice(tiersBySlug[slug].amount_cents)} / Monat
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((row, i) => {
                const isHighlight = row.highlight;
                return (
                  <tr
                    key={row.label}
                    data-testid={`preise-row-${i}${isHighlight ? "-highlight" : ""}`}
                    className={
                      isHighlight
                        ? "bg-peach-50 border-y-2 border-terra/30"
                        : i % 2 === 0 ? "bg-cream-50/40" : ""
                    }
                  >
                    <td className="px-4 md:px-5 py-3 text-[13.5px] text-sand-800 leading-snug align-top">
                      <div className="flex items-start gap-2">
                        {isHighlight && (
                          <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] uppercase tracking-[0.18em] font-bold bg-terra text-white">
                            USP
                          </span>
                        )}
                        <div>
                          <div className={isHighlight ? "font-semibold text-sand-900" : ""}>
                            {row.label}
                          </div>
                          {row.sublabel && (
                            <div className="text-[12.5px] md:text-[13px] text-sand-700 mt-1 leading-snug">
                              {row.sublabel}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {HEADLINE_TIERS.map((slug) => (
                      <td
                        key={slug}
                        className="px-3 md:px-4 py-3 text-center align-middle"
                        data-testid={`preise-row-${i}-${slug}`}
                      >
                        <ValueCell raw={row.values[slug]} />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Träger-Hinweis entfernt auf Wunsch — Träger-Akquise läuft separat über Sales-Gespräch */}
    </div>
  );
}
